import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

export const fetchAssistantHistory = createAsyncThunk(
  "assistant/fetchHistory",
  async (sessionId, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/assistant/sessions/${sessionId}/messages/`, { method: "GET" });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка загрузки истории" });
    }
  }
);

// Resumes the user's most recent chat session (server-side, per-user via ChatSession.user)
// instead of starting a brand new empty one every time the widget is reopened/the page is
// reloaded. Falls back to sessionId 0 ("create new" — see AiChatConsumer.connect) when the
// user has no prior sessions.
export const resumeLatestSession = createAsyncThunk(
  "assistant/resumeLatestSession",
  async (_, { rejectWithValue }) => {
    try {
      const sessions = await apiFetch("/api/assistant/sessions/", { method: "GET" });
      return Array.isArray(sessions) && sessions.length > 0 ? sessions[0].id : 0;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка загрузки сессии" });
    }
  }
);

// Удаляет текущую сессию вместе со всей перепиской (физическое удаление, у ChatMessage
// стоит CASCADE). Сокет закрывает вызывающая сторона ДО этого запроса: консьюмер держит
// ChatSession в памяти и после удаления строки молча умирает, не прислав ни error, ни done.
export const deleteAssistantSession = createAsyncThunk(
  "assistant/deleteSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      await apiFetch(`/api/assistant/sessions/${sessionId}/`, { method: "DELETE" });
      return sessionId;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка удаления истории" });
    }
  }
);

// Стартовые подсказки: сервер знает Scope роли и отдаёт только те вопросы, которые она
// реально может выполнить (см. assistant/suggestions.py). Текущую страницу шлём сырым
// pathname — сопоставление с разделом делает бэкенд.
export const fetchAssistantSuggestions = createAsyncThunk(
  "assistant/fetchSuggestions",
  async (pathname, { rejectWithValue }) => {
    try {
      const query = pathname ? `?path=${encodeURIComponent(pathname)}` : "";
      const data = await apiFetch(`/api/assistant/suggestions/${query}`, { method: "GET" });
      return { data, pathname };
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Ошибка загрузки подсказок" });
    }
  }
);

let nextLocalId = -1;

const assistantSlice = createSlice({
  name: "assistant",
  initialState: {
    isOpen: false,
    panelSize: "half", // "half" | "full"
    connectionStatus: "idle", // idle | connecting | open | closed | error
    sessionId: null,
    sessionResolved: false, // true once resumeLatestSession has decided which session to use
    // idle | loading | loaded | failed — gates sending until the initial history GET settles,
    // see the fetchAssistantHistory.fulfilled comment below for why this matters.
    historyStatus: "idle",
    messages: [], // {id, role, text, chartSpec, isStreaming}
    draft: "",
    error: null,
    suggestions: {
      greeting: "",
      items: [], // {key, text, tool, contextual}
      teaser: null,
      page: null,
      status: "idle", // idle | loading | ready | failed
      loadedPath: null, // для какого pathname загружено — чтобы не дёргать API повторно
    },
    pendingQuestion: null, // вопрос из тизера, ждёт открытия сокета
  },
  reducers: {
    toggleOpen(state) {
      state.isOpen = !state.isOpen;
    },
    setPanelSize(state, action) {
      state.panelSize = action.payload === "full" ? "full" : "half";
    },
    togglePanelSize(state) {
      state.panelSize = state.panelSize === "full" ? "half" : "full";
    },
    setDraft(state, action) {
      state.draft = action.payload;
    },
    setConnectionStatus(state, action) {
      state.connectionStatus = action.payload;
    },
    sessionStarted(state, action) {
      state.sessionId = action.payload;
    },
    userMessageSent(state, action) {
      state.messages.push({ id: nextLocalId--, role: "user", text: action.payload, chartSpec: null });
      state.messages.push({
        id: nextLocalId--,
        role: "assistant",
        text: "",
        pendingText: "", // buffered chunks not yet "typed" out — see revealNextChars
        chartSpec: null,
        isStreaming: true,
      });
    },
    assistantChunkReceived(state, action) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant" && last.isStreaming) {
        // Buffered, not appended directly — a typewriter reveal loop in the widget
        // (AssistantWidget.js) drains this into `text` a few characters at a time, so
        // the UI reads at a GPT-chat pace instead of dumping whole network chunks at once.
        last.pendingText = (last.pendingText || "") + action.payload;
      }
    },
    revealNextChars(state, action) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.pendingText) {
        const n = action.payload || 1;
        last.text += last.pendingText.slice(0, n);
        last.pendingText = last.pendingText.slice(n);
      }
    },
    assistantChartReceived(state, action) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant" && last.isStreaming) {
        last.chartSpec = action.payload;
      }
    },
    assistantToolResultReceived(state, action) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant" && last.isStreaming) {
        last.toolResult = action.payload;
      }
    },
    assistantLinksReceived(state, action) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant" && last.isStreaming) {
        // Deep-links from the open_page tool — a message may accrue several if the model
        // calls open_page more than once in a turn.
        last.links = [...(last.links || []), ...(action.payload || [])];
      }
    },
    assistantMessageDone(state) {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant") {
        // Flush any buffered-but-not-yet-typed text immediately — no lost characters
        // even if the typewriter reveal loop was slower than the network stream.
        if (last.pendingText) {
          last.text += last.pendingText;
          last.pendingText = "";
        }
        last.isStreaming = false;
      }
    },
    assistantError(state, action) {
      state.error = action.payload;
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "assistant" && last.isStreaming) {
        last.isStreaming = false;
        if (!last.text) last.text = "Ошибка ассистента. Попробуйте ещё раз.";
      }
    },
    clearError(state) {
      state.error = null;
    },
    // Локальный сброс после удаления сессии на сервере. Отдельный reducer, а не только
    // case на deleteAssistantSession.fulfilled — тем же путём чистится случай, когда на
    // сервере ещё ничего не сохранено и удалять нечего.
    historyCleared(state) {
      state.messages = [];
      state.historyStatus = "idle";
      // 0 == «создать новую сессию» (см. AiChatConsumer.connect). Намеренно НЕ сбрасываем
      // sessionResolved в false: иначе отработает resumeLatestSession и вернёт предыдущую
      // сессию со старой перепиской — то есть очистка визуально «не сработает».
      state.sessionId = 0;
      state.sessionResolved = true;
      state.connectionStatus = "closed";
      state.error = null;
      // Иначе эффект автоотправки переотправит вопрос из тизера сразу после подъёма
      // нового сокета и тут же заполнит только что очищенную историю.
      state.pendingQuestion = null;
    },
    pendingQuestionQueued(state, action) {
      state.pendingQuestion = action.payload;
    },
    pendingQuestionCleared(state) {
      state.pendingQuestion = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssistantHistory.pending, (state) => {
        state.historyStatus = "loading";
      })
      .addCase(fetchAssistantHistory.fulfilled, (state, action) => {
        state.historyStatus = "loaded";
        // Регресс: onOpen дёргает этот запрос в тот же момент, когда сокет становится
        // "open" — ровно тогда же может уйти автоматическое сообщение из тизера. Если ответ
        // сервера (обычно пустая история) приходит, пока это сообщение уже печатается,
        // безусловная перезапись стирала стримящийся пузырь, и дальнейшие куски текста от
        // ассистента молча терялись (см. AssistantWidget.test.js). Пока идёт стрим — не трогаем.
        const last = state.messages[state.messages.length - 1];
        if (last && last.isStreaming) return;
        state.messages = (action.payload || []).map((m) => ({
          id: m.id,
          role: m.role,
          text: m.content,
          chartSpec: m.chart_spec,
          isStreaming: false,
        }));
      })
      .addCase(fetchAssistantHistory.rejected, (state, action) => {
        state.historyStatus = "failed";
        state.error = action.payload?.detail || "Ошибка загрузки истории";
      })
      .addCase(resumeLatestSession.fulfilled, (state, action) => {
        state.sessionId = action.payload;
        state.sessionResolved = true;
      })
      .addCase(resumeLatestSession.rejected, (state, action) => {
        // Fall back to "create new" rather than blocking the widget forever on a
        // transient history-lookup failure.
        state.sessionId = 0;
        state.sessionResolved = true;
        state.error = action.payload?.detail || "Ошибка загрузки сессии";
      })
      .addCase(deleteAssistantSession.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteAssistantSession.rejected, (state, action) => {
        state.error = action.payload?.detail || "Ошибка удаления истории";
        // Сокет к этому моменту уже закрыт вызывающей стороной — если ничего не сделать,
        // виджет останется немым навсегда. Переводим на новую сессию, как и при успехе.
        state.sessionId = 0;
        state.sessionResolved = true;
        state.connectionStatus = "closed";
      })
      .addCase(fetchAssistantSuggestions.pending, (state) => {
        state.suggestions.status = "loading";
      })
      .addCase(fetchAssistantSuggestions.fulfilled, (state, action) => {
        const { data, pathname } = action.payload;
        state.suggestions.greeting = data?.greeting || "";
        state.suggestions.items = data?.suggestions || [];
        state.suggestions.teaser = data?.teaser || null;
        state.suggestions.page = data?.page || null;
        state.suggestions.status = "ready";
        state.suggestions.loadedPath = pathname ?? null;
      })
      .addCase(fetchAssistantSuggestions.rejected, (state) => {
        // Намеренно не пишем в state.error: этот баннер — для ошибок чата, а подсказки
        // необязательны. Панель просто покажет прежний текст пустого состояния.
        state.suggestions.status = "failed";
      });
  },
});

export const {
  toggleOpen,
  setPanelSize,
  togglePanelSize,
  setDraft,
  setConnectionStatus,
  sessionStarted,
  userMessageSent,
  assistantChunkReceived,
  revealNextChars,
  assistantChartReceived,
  assistantToolResultReceived,
  assistantLinksReceived,
  assistantMessageDone,
  assistantError,
  clearError,
  historyCleared,
  pendingQuestionQueued,
  pendingQuestionCleared,
} = assistantSlice.actions;

export const selectAssistant = (state) => state.assistant;
export const selectAssistantSuggestions = (state) => state.assistant.suggestions;

export default assistantSlice.reducer;
